'use client';

import { CityPageQueryQuery } from '@medprev-web-app/api-graphql-client';
import Image from 'next/image';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { BrandSectionTopic } from './brand-section-topic.component';
import marceloSerradoCard from './assets/marcelo-serrado-card.png';

import { BranchScheduleButton } from '@/app/(main)/(pages)/_components/search-section/buttons/branch-schedule-button.component';
import { getServiceChannelFlags } from '@/app/(main)/(pages)/_utils/business-model.util';
import { MedprevOnlineScheduleButton } from '@/app/(main)/(pages)/_components/search-section/buttons/medprev-online-schedule-button.component';
import { SearchSectionCtaButtons } from '@/app/(main)/(pages)/_components/search-section/common/search-section-cta-buttons.component';
import { ContentContainer } from '@/components/ui/content-container.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface BrandSectionProps {
  cityConfig?: CityPageQueryQuery;
  isBranchPage?: boolean;
  branchWhatsapp?: string;
}

export const BrandSection = ({
  cityConfig,
  isBranchPage = false,
  branchWhatsapp = '',
}: BrandSectionProps) => {
  const { isNoOnlineWithBranch } = getServiceChannelFlags(cityConfig);
  const analytics = getAnalyticsPlugin();

  const contactDescription = isNoOnlineWithBranch
    ? 'Mande uma mensagem para nossa equipe e agende sua consulta ou exame.'
    : 'Escolha se prefere agendar pelo site, app ou com a ajuda de um atendente.';

  return (
    <section
      data-testid={DataTestIdEnum.CITY_BRAND_SECTION}
      className="pt-10 border-y bg-gray-100 border-gray-300"
    >
      <Typography
        variant={TypographyVariant.LARGE_SUBTITLE}
        className="xl:hidden font-bold text-gray-900 text-center mb-6"
      >
        Veja como é fácil agendar!
      </Typography>

      <ContentContainer className="flex flex-col md:flex-row-reverse gap-10 md:gap-6 xl:gap-10">
        <div className="flex flex-col gap-6 md:justify-center w-full md:mb-6 xl:mb-0">
          <Typography
            variant={TypographyVariant.LARGE_SUBTITLE}
            className="hidden xl:block font-bold text-gray-900"
          >
            Veja como é fácil agendar!
          </Typography>

          <BrandSectionTopic
            title="Entre em contato conosco"
            description={contactDescription}
          />

          <BrandSectionTopic
            title="Agendamento imediato"
            description="Veja as opções disponíveis na sua cidade."
          />

          <BrandSectionTopic
            title="Pague por valores reduzidos"
            description="Para garantir seu agendamento."
          />

          {isBranchPage
            ? (
                <BranchScheduleButton
                  variant="default"
                  branchesLength={1}
                  whatsapp={branchWhatsapp}
                  onClick={() => {
                    analytics.track(AnalyticsEnum.CLICK_CITY_BUTTON_SCHEDULE_BRANCH, {
                      secao_origem: 'beneficios',
                      nome_secao: 'vantagens_medprev',
                    });
                  }}
                />
              )
            : cityConfig
              ? (
                  <div className="flex flex-col xl:flex-row gap-x-2 gap-y-4">
                    <SearchSectionCtaButtons
                      cityConfig={cityConfig}
                      firstButtonColor="primary"
                      secondButtonVariant="text"
                      medprevScheduleButtonAnalyticsEventName={AnalyticsEnum.CLICK_CITY_BUTTON_SCHEDULE_BY_WEBSITE}
                      assistedPurchaseButtonAnalyticsEventName={AnalyticsEnum.CLICK_CITY_BUTTON_SCHEDULE_SUPPORT}
                      telemedicineButtonAnalyticsEventName={AnalyticsEnum.CLICK_CITY_BUTTON_TELEHEALTH}
                      from="beneficios"
                      sessionName="vantagens_medprev"
                    />
                  </div>
                )
              : (
                  <MedprevOnlineScheduleButton
                    color="primary"
                    titleButton="Agendar agora"
                    data-analytics-event={AnalyticsEnum.CLICK_SCHEDULE_NOW_HOME_PAGE}
                    from="beneficios"
                    sessionName="vantagens_medprev"
                  />
                )}
        </div>
        <div className="flex items-end justify-center md:justify-start w-full">
          <Image
            src={marceloSerradoCard}
            alt="Colaborador"
            priority
            fetchPriority="high"
            draggable={false}
            className="h-[21.563rem] sm:h-[28.5rem] md:h-[20rem] xl:h-[27.5rem] w-fit"
          />
        </div>
      </ContentContainer>
    </section>
  );
};
